import React, { useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Chip, Box, CircularProgress,
  Avatar, Fade, Grow, Stack
} from '@mui/material';
import { Link } from 'react-router-dom';
import { getServers, getServerState, getServerStats } from '../api';
import { DEFAULT_SERVER_SETTINGS } from '../constants';
import DnsIcon from '@mui/icons-material/Dns';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GroupIcon from '@mui/icons-material/Group';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';

function ServerCardItem({ server, index }) {
  const [status, setStatus] = useState('unknown');
  const [playerInfo, setPlayerInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const { data } = await getServerState(server.name);
        if (mounted) setStatus(data.state);
      } catch (e) {
        if (mounted) setStatus('error');
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [server.name]);

  useEffect(() => {
    if (status !== 'running') {
      setPlayerInfo(null);
      return;
    }
    let mounted = true;
    const fetchStats = async () => {
      try {
        const { data } = await getServerStats(server.name);
        if (mounted && data.players) {
          setPlayerInfo(data.players);
        }
      } catch {}
    };
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [server.name, status]);

  const type = server.server_settings?.TYPE || DEFAULT_SERVER_SETTINGS.TYPE;
  const version = server.server_settings?.VERSION || DEFAULT_SERVER_SETTINGS.VERSION;

  const getStatusColor = (s) => {
    switch (s) {
      case 'running': return '#00E676';
      case 'exited': return '#FF1744';
      default: return '#FF9100';
    }
  };

  const statusColor = getStatusColor(status);

  return (
    <Grow in={true} timeout={300 + (index * 100)}>
      <Grid item xs={12} sm={6} md={4}>
        <Card
          component={Link}
          to={`/server/${server.name}`}
          sx={{
            display: 'block',
            textDecoration: 'none',
            height: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            border: `1px solid rgba(255, 255, 255, 0.06)`,
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: `0 12px 30px rgba(0,0,0,0.5), 0 0 20px ${statusColor}1A`,
              borderColor: statusColor,
              '& .arrow-icon': {
                transform: 'translateX(4px)',
                color: statusColor,
              }
            }
          }}
        >
          {/* Top glowing accent line */}
          <Box sx={{
            height: '3px',
            width: '100%',
            backgroundColor: statusColor,
            boxShadow: `0 1px 10px ${statusColor}`
          }} />

          <CardContent sx={{ p: 3 }}>
            {/* Header: Avatar, Name, Status indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar variant="rounded" sx={{
                  bgcolor: `${statusColor}15`,
                  color: statusColor,
                  border: `1px solid ${statusColor}30`,
                  boxShadow: `0 0 10px ${statusColor}10`
                }}>
                  <DnsIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{
                    fontWeight: '800',
                    color: '#fff',
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '1.25rem',
                    lineHeight: 1.2
                  }}>
                    {server.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    ID: {server.container_id?.substring(0, 8)}
                  </Typography>
                </Box>
              </Box>

              <Chip
                label={status.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: `${statusColor}12`,
                  color: statusColor,
                  border: `1px solid ${statusColor}30`,
                  fontWeight: '800',
                  fontSize: '0.65rem',
                  letterSpacing: '0.5px'
                }}
              />
            </Box>

            {/* Middle: Engine type and Address info */}
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={type}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0,176,255,0.08)',
                    color: '#00B0FF',
                    border: '1px solid rgba(0,176,255,0.15)',
                    fontSize: '0.7rem'
                  }}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  v{version}
                </Typography>
              </Box>

              <Box sx={{
                bgcolor: 'rgba(0,0,0,0.2)',
                p: 1,
                px: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.03)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ElectricalServicesIcon sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {server.address}
                </Typography>
              </Box>
            </Stack>

            {/* Footer: Players or details arrow */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 2,
              borderTop: '1px solid rgba(255,255,255,0.05)'
            }}>
              <Box>
                {playerInfo ? (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: '#00E676' }}>
                    <GroupIcon fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {playerInfo.online} / {playerInfo.max} Players
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Server is offline
                  </Typography>
                )}
              </Box>

              <Box className="arrow-icon" sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                transition: 'all 0.2s ease-in-out'
              }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5, fontFamily: 'Outfit, sans-serif' }}>
                  MANAGE
                </Typography>
                <ArrowForwardIcon fontSize="small" />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grow>
  );
}

export default function ServerList() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    try {
      const { data } = await getServers();
      setServers(data);
    } catch (e) {
      console.error("Failed to fetch servers", e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  if (servers.length === 0) {
    return (
      <Fade in={true}>
        <Card sx={{
          p: 5,
          textAlign: 'center',
          background: 'rgba(30,30,40,0.2)',
          border: '1px dashed rgba(255,255,255,0.1)',
          boxShadow: 'none',
          borderRadius: 4
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>
            No Active Minecraft Servers Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deploy your very first secure high-speed game server to get started!
          </Typography>
        </Card>
      </Fade>
    );
  }

  return (
    <Grid container spacing={3}>
      {servers.map((server, index) => (
        <ServerCardItem key={server.name} server={server} index={index} />
      ))}
    </Grid>
  );
}